import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", service: "", message: "" };

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3.5 text-white placeholder:text-white/30 text-sm outline-none transition-colors duration-200 focus:border-cyan-glass";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Bitte füllen Sie Name, E-Mail, Telefon und Leistung aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde übermittelt. Wir melden uns zeitnah.");
      setForm(empty);
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte rufen Sie uns an: " + COMPANY.phone);
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Telefon", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
    { icon: Mail, label: "E-Mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Adresse", value: `${COMPANY.street}, ${COMPANY.zip}` },
  ];

  return (
    <section id="kontakt" className="relative bg-[#030712] py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(0,229,255,0.08),transparent_50%)]" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Left */}
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-glass mb-6">
            Angebot anfordern
          </p>
          <h2 className="font-heading text-5xl md:text-7xl font-semibold tracking-tighter text-white leading-[0.95] mb-10">
            Bereit für den <span className="text-cyan-glass text-glow">Durchblick?</span>
          </h2>
          <p className="text-base md:text-lg font-light text-white/60 max-w-md mb-12">
            Fordern Sie Ihr unverbindliches Angebot an — wir melden uns schnellstmöglich bei Ihnen zurück.
          </p>

          <div className="space-y-6">
            {contactItems.map((c) => (
              <div key={c.label} className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/10 text-cyan-glass">
                  <c.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{c.label}</div>
                  {c.href ? (
                    <a href={c.href} className="text-white hover:text-cyan-glass transition-colors" data-testid={`contact-${c.label.toLowerCase()}`}>
                      {c.value}
                    </a>
                  ) : (
                    <span className="text-white">{c.value}</span>
                  )}
                </div>
              </div>
            ))}
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/10 text-cyan-glass">
                <Clock className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Öffnungszeiten</div>
                {COMPANY.hours.map((h) => (
                  <div key={h.d} className="text-sm text-white/80">
                    <span className="text-white/50">{h.d}:</span> {h.h}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-testid="quote-form"
          className="glass-strong rounded-sm p-8 md:p-10 space-y-5 h-fit"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input className={inputCls} placeholder="Ihr Name *" value={form.name} onChange={set("name")} data-testid="input-name" />
            <input className={inputCls} placeholder="Telefon *" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
          </div>
          <input className={inputCls} type="email" placeholder="E-Mail *" value={form.email} onChange={set("email")} data-testid="input-email" />

          <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
            <SelectTrigger className={inputCls + " h-auto justify-between data-[placeholder]:text-white/30"} data-testid="select-service">
              <SelectValue placeholder="Gewünschte Leistung *" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A111F] border-white/15 text-white max-h-72">
              {SERVICES.map((s) => (
                <SelectItem key={s.key} value={s.name} className="focus:bg-cyan-glass/15 focus:text-cyan-glass">
                  {s.name}
                </SelectItem>
              ))}
              <SelectItem value="Sonstiges / Beratung" className="focus:bg-cyan-glass/15 focus:text-cyan-glass">
                Sonstiges / Beratung
              </SelectItem>
            </SelectContent>
          </Select>

          <textarea
            className={inputCls + " min-h-[120px] resize-none"}
            placeholder="Ihre Nachricht (optional)"
            value={form.message}
            onChange={set("message")}
            data-testid="input-message"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-quote"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-glass px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-[#030712] font-medium transition-transform duration-200 active:scale-[0.98] hover:shadow-[0_0_40px_rgba(0,229,255,0.5)] disabled:opacity-60"
          >
            {loading ? "Wird gesendet…" : "Anfrage absenden"}
            <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
